import { styled } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import downloadFile from "_shared/_utilities/download-file";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import { DocumentFileType } from "documents/types/document-file-type";
import { filesize } from "filesize";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { enqueueSnackbar } from "notistack";
import { defaultStyles, FileIcon } from "react-file-icon";
import Session from "users/__session/session";

const AttachmentItemIcon = styled(ListItemIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  maxWidth: "32px",
  minWidth: "16px",
}));
const SecondaryTextContainer = styled("span")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  width: "100%",
}));

type AttachmentListItemProps = {
  session: Session;
  forumId: Guid | undefined;
  attachmentInfo: MessageFileInfo;
};

export default function AttachmentListItem(props: Readonly<AttachmentListItemProps>) {
  const { session, forumId, attachmentInfo } = props;

  async function handleClick() {
    try {
      if (!forumId) {
        throw new Error("Forum ID is undefined");
      }
      const messagingService = new MessagingAPIService(session);
      const response = await messagingService.getMessageAttachmentById(forumId, attachmentInfo.id);
      await downloadFile(response, attachmentInfo.name);
    } catch (error) {
      console.error("Error downloading file:", error);
      enqueueSnackbar("Error downloading file", { variant: "error" });
    }
  }

  return (
    <ListItemButton disableGutters onClick={handleClick}>
      <AttachmentItemIcon>
        <FileIcon
          extension={enumKeyOf(DocumentFileType, attachmentInfo.name.split(".")[1]).toLocaleLowerCase()}
          {...defaultStyles[attachmentInfo.name.split(".")[1].toLocaleLowerCase() as keyof typeof defaultStyles]}
        />
      </AttachmentItemIcon>
      <ListItemText
        primary={attachmentInfo.name}
        secondary={
          <SecondaryTextContainer>
            <span>{filesize(attachmentInfo.fileSize ?? 0)}</span>
            <span>{attachmentInfo.createdDate.format("MMM D, YYYY h:mm A")}</span>
          </SecondaryTextContainer>
        }
        slotProps={{
          primary: {
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      />
    </ListItemButton>
  );
}
